import { z } from "zod"
import {
  OrderDirectionEnum,
  PaginationMetadataInputType,
  PaginationMetadataOutputSchema,
  PaginationMetadataOutputType,
} from "@antipartido-inc/contracts-ifaces"

const PaginationValidation = z.object({
  offset: z.number({ required_error: "offset is required" }),
  limit: z.number({ required_error: "limit is required" }),
  orderBy: z.string({ required_error: "orderBy is required" }),
  orderDirection: z.nativeEnum(OrderDirectionEnum),
  numElements: z.number({ required_error: "numElements is required" }).optional(),
})

export type PaginationInput = PaginationMetadataInputType
export type PaginationSerialized = ReturnType<Pagination["serialize"]>

export class Pagination {
  constructor(
    private readonly _offset: number,
    private readonly _limit: number,
    private readonly _orderDirection: OrderDirectionEnum,
    private readonly _orderBy: string,
    private readonly _numElements?: number,
  ) {}

  static create({ offset, limit, orderBy, orderDirection, numElements }: z.infer<typeof PaginationValidation>) {
    const { success, error } = PaginationValidation.safeParse({ offset, limit, orderBy, orderDirection, numElements })
    if (!success) throw new Error(error.message)
    return new Pagination(offset, limit, orderDirection, orderBy, numElements)
  }

  static createFromAPI({ offset, limit, orderBy, orderDirection, numElements }: PaginationMetadataOutputType) {
    const { success, error } = PaginationMetadataOutputSchema.safeParse({
      offset,
      limit,
      orderBy,
      orderDirection,
      numElements,
    })
    if (!success) throw new Error(error.message)
    return new Pagination(offset, limit, orderDirection, orderBy, numElements ?? undefined)
  }

  static createDefault({ orderBy }: { orderBy: string }) {
    return new Pagination(0, 20, OrderDirectionEnum.DESC, orderBy)
  }

  get offset() { return this._offset } // prettier-ignore
  get limit() { return this._limit } // prettier-ignore
  get orderBy() { return this._orderBy } // prettier-ignore
  get orderDirection() { return this._orderDirection } // prettier-ignore
  get numElements() { return this._numElements } // prettier-ignore

  toInput(): PaginationMetadataInputType {
    return {
      offset: this.offset,
      limit: this.limit,
      orderBy: this.orderBy,
      orderDirection: this.orderDirection,
    }
  }

  serialize() {
    return {
      offset: this.offset,
      limit: this.limit,
      orderBy: this.orderBy,
      orderDirection: this.orderDirection,
      numElements: this.numElements ?? undefined,
    }
  }
}
